import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { V, Haptic } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { Card, Btn, Field, Sheet, Chip, Stat, Progress, Skeleton, SkeletonCard, SuccessToastCtrl, ConfirmCtrl, MsgBannerCtrl } from '../../components/ui';
import { today, ago, fmtShort, fmtFull, uid, calc1RM, calcPlates, PLATES, convW, wUnit, dUnit, isCardio, toKg, toLbs, friendDisplayName, chartCfg } from '../../utils/helpers';
import { CloudSync, SocialAPI, SyncQueue, SYNC_URL, APP_VERSION } from '../../utils/sync';
import { apiPost } from '../../utils/api';
import { Undo } from '../../utils/undo';
import { useLayout } from '../../utils/responsive';
import { ShareCard } from '../../utils/share';
import { BADGE_DEFS, calcEarnedBadges } from '../../data/badges';
import { TEMPLATES } from '../../data/templates';
import { IRON_RANKS } from '../../data/ranks';
import { FF_HACKS, FF_BRANDS } from '../../data/hacks';
import { FOODS } from '../../data/foods';
import { AuthToken } from '../../utils/auth';
import { calcReadiness, useStreak, usePRs, useNutritionStreak } from '../../components/dialogs';
import { normalizePhasesFromWire, phaseIsActive, injuryIsActive, injuryJoint } from '../../utils/healthWire';
import { onAccent } from '../../components/kit';

export function AICoachChat({s}){
  const { isDesktop } = useLayout();
  const [userKey,setUserKey]=useState(null);
  const [keyLoaded,setKeyLoaded]=useState(false);
  // Load API key from encrypted storage on mount
  useEffect(()=>{
    LS.getSecure("ft-anthropic-key").then(k=>{setUserKey(k||null);setKeyLoaded(true);});
  },[]);
  const defaultMsg=[{role:"assistant",content:userKey
    ?"Hey! I'm your AI coach. Ask me anything about your training, nutrition, or recovery. I have access to all your workout data."
    :"Welcome to AI Coach! To get started, you'll need your own Anthropic API key. Paste it in the field above — it stays on your device and never touches our servers. Get a key at console.anthropic.com."}];
  const [msgs,setMsgs]=useState(LS.get("ft-ai-chat")||defaultMsg);
  const aiStreak=useStreak(s.workouts);
  const [input,setInput]=useState("");
  const [loading,setLoading]=useState(false);
  const scrollRef=useRef(null);
  const [apiKeyInput,setApiKeyInput]=useState("");
  // Reset chat when API key is first added (so welcome message updates)
  useEffect(()=>{
    if(userKey&&msgs.length===1&&msgs[0].role==="assistant"&&msgs[0].content.includes("API key")){
      setMsgs([{role:"assistant",content:"Hey! I'm your AI coach. Ask me anything about your training, nutrition, or recovery. I have full access to your workout data."}]);
    }
  },[userKey]);
  // Persist messages
  useEffect(()=>{if(msgs.length>1)LS.set("ft-ai-chat",msgs.slice(-20));},[msgs]);

  const buildContext=()=>{
    const rw=s.workouts.slice(0,5).map(w=>`${w.date}: ${w.exercises.map(e=>`${s.exercises.find(x=>x.id===e.exerciseId)?.name||e.exerciseId} (${e.sets.length} sets)`).join(", ")}`).join("\n");
    const bw=s.body[0]?`Current weight: ${s.body[0].weight} ${wUnit(s.units)}`:"";
    const goals=s.goals?`Goals: ${s.goals.cal}cal, ${s.goals.protein}g protein`:"";
    const activePhase=normalizePhasesFromWire(s.phases||[]).find(p=>phaseIsActive(p));
    const phase=activePhase?`Current phase: ${activePhase.type}${activePhase.notes?` (${activePhase.notes})`:""}`:"";
    const sched=s.schedule?.weekly||{};
    const todayType=s.schedule?.overrides?.[today()]||sched[new Date().getDay()]||"Rest";
    const schedule=`Today is ${todayType} day. Weekly: ${Object.values(sched).join(", ")}`;
    
    // InjuryManager stores {joint,severity,notes}; older code looked for name/area and always found nothing
    const injuries=(s.injuries||[]).filter(injuryIsActive).map(inj=>`${injuryJoint(inj).replace(/_/g," ")}${inj.severity?` (${inj.severity})`:""}`).filter(Boolean).join(", ");
    const prefs=[s.profile?.equipment?`Equipment: ${s.profile.equipment}`:"",s.profile?.sessionLength?`Session length: ${s.profile.sessionLength} min`:"",s.profile?.diet?`Diet: ${s.profile.diet}`:"",(s.profile?.allergies||[]).length?`Allergies: ${s.profile.allergies.join(", ")}`:""].filter(Boolean).join("\n");
    return`You are IRONLOG AI Coach. Be concise, actionable, encouraging. Max 3 paragraphs.\n\nUser data:\n${bw}\n${goals}\n${phase}\n${schedule}\nStreak: ${aiStreak} days\n${injuries?`Active injuries: ${injuries}`:""}\n${prefs}\n\nRecent workouts:\n${rw}`;
  };

  const send=async()=>{
    if(!input.trim()||!userKey)return;
    const userMsg={role:"user",content:input.trim()};
    setMsgs(m=>[...m,userMsg]);setInput("");setLoading(true);
    try{
      const res=await fetch("https://api.anthropic.com/v1/messages",{
        method:"POST",
        headers:{"Content-Type":"application/json","x-api-key":userKey,"anthropic-version":"2023-06-01",
          "anthropic-dangerous-direct-browser-access":"true"},
        body:JSON.stringify({model:"claude-opus-5",max_tokens:1024,
          system:buildContext(),
          // The Messages API requires the first message to be a `user` turn — the stored
          // history starts with the assistant welcome, which made every request 400.
          messages:(()=>{const h=[...msgs.filter(m=>m.role!=="system"),userMsg];const i=h.findIndex(m=>m.role==="user");
            return h.slice(i<0?0:i).map(m=>({role:m.role,content:m.content}));})()
        })
      });
      const data=await res.json();
      if(data.error){
        setMsgs(m=>[...m,{role:"assistant",content:`Error: ${data.error.message||"Invalid API key or request failed."}. Check your key in your Anthropic dashboard.`}]);
      }else{
        const reply=data.content?.[0]?.text||"Sorry, I couldn't process that. Try again.";
        setMsgs(m=>[...m,{role:"assistant",content:reply}]);
      }
    }catch(e){
      setMsgs(m=>[...m,{role:"assistant",content:"Connection failed. Check your internet connection."}]);
    }
    setLoading(false);
    setTimeout(()=>scrollRef.current?.scrollTo(0,scrollRef.current.scrollHeight),100);
  };

  return(
    <div style={{display:"flex",flexDirection:"column",height:"calc(var(--content-h, 100vh - 180px) - 48px)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div>
          <div style={{fontSize:16,fontWeight:800,color:V.text}}>AI Coach</div>
          <div style={{fontSize:11,color:userKey?V.accent:V.warn}}>{userKey?"Using your API key":"API key required"}</div>
        </div>
        {msgs.length>1&&<button onClick={()=>{setMsgs(defaultMsg);LS.set("ft-ai-chat",null);SuccessToastCtrl.show("Conversation cleared");}}
          style={{padding:"4px 10px",borderRadius:6,background:V.inputBg,border:`1px solid ${V.cardBorder}`,
            cursor:"pointer",fontSize:11,color:V.text3,fontFamily:V.font}}>Clear</button>}
      </div>
      {!userKey&&(
        <div style={{display:"flex",gap:6,marginBottom:8}}>
          <input value={apiKeyInput||""} onChange={e=>setApiKeyInput(e.target.value)} placeholder="Paste Anthropic API key for unlimited"
            type="password" style={{flex:1,padding:"8px 10px",background:V.card,border:`1px solid ${V.cardBorder}`,
              borderRadius:8,color:V.text,fontSize:11,outline:"none",fontFamily:V.mono}}/>
          <button onClick={async ()=>{if(apiKeyInput?.trim()){
              const ok=await LS.setSecure("ft-anthropic-key",apiKeyInput.trim());
              if(!ok){SuccessToastCtrl.error("Couldn't store the key securely on this device");return;}
              setUserKey(apiKeyInput.trim());SuccessToastCtrl.show("API key saved");
            }}}
            style={{padding:"6px 10px",borderRadius:6,background:`${V.accent}15`,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,color:V.accent}}>Save</button>
        </div>
      )}
      {userKey&&(
        <button onClick={()=>{LS.del("ft-anthropic-key");setUserKey(null);SuccessToastCtrl.show("API key removed");}}
          style={{padding:"4px 10px",borderRadius:6,background:V.inputBg,border:`1px solid ${V.cardBorder}`,
            cursor:"pointer",fontSize:11,color:V.text3,fontFamily:V.font,marginBottom:8,alignSelf:"flex-start"}}>Remove API Key</button>
      )}
      {/* Messages */}
      <div ref={scrollRef} style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:8,paddingBottom:8}}>
        {msgs.map((m,i)=>(
          <div key={i} style={{padding:"10px 12px",borderRadius:12,maxWidth:"85%",lineHeight:1.5,fontSize:12,
            ...(m.role==="user"?{alignSelf:"flex-end",background:`${V.accent}15`,color:V.text}
              :{alignSelf:"flex-start",background:V.inputBg,color:V.text2})}}>
            {m.content}
          </div>
        ))}
        {loading&&<div style={{alignSelf:"flex-start",padding:"10px 12px",borderRadius:12,background:V.inputBg,fontSize:12,color:V.text3}}>Thinking...</div>}
      </div>
      {/* Input */}
      <div style={{display:"flex",gap:8,paddingTop:8}}>
        <input value={input} onChange={e=>setInput(e.target.value)} placeholder={userKey?"Ask your coach...":"Add your API key above to start"}
          disabled={!userKey} onKeyDown={e=>{if(e.key==="Enter")send();}}
          style={{flex:1,padding:"10px 14px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
            borderRadius:10,color:V.text,fontSize:13,outline:"none",fontFamily:V.font,opacity:userKey?1:0.5}}/>
        <Btn onClick={send} disabled={!input.trim()||loading||!userKey}>{Icons.check({size:14,color:onAccent()})}</Btn>
      </div>
    </div>
  );
}

